import { useCallback, useRef, type MutableRefObject } from 'react';
import type { HiddenInputController, HiddenInputControllerFactoryArgs, HiddenInputValueMode } from '../types';
import { useHiddenInputAdapter } from './use-hidden-input-adapter';

type HiddenInputControllerInternal = HiddenInputController & {
    lastModeRef: MutableRefObject<HiddenInputValueMode>;
    manualValueRef: MutableRefObject<string | undefined>;
    lastValueRef: MutableRefObject<string>;
};

const useHiddenInputController = ({ forwardedRef, onChange }: HiddenInputControllerFactoryArgs): HiddenInputControllerInternal => {
    const { hiddenInputRef, setHiddenInputValue, handleHiddenChange } = useHiddenInputAdapter({ forwardedRef, onChange });

    const lastModeRef = useRef<HiddenInputValueMode>('derived');
    const manualValueRef = useRef<string | undefined>(undefined);
    const lastValueRef = useRef<string>('');

    const setHiddenValue = useCallback(
        (next: string, mode: HiddenInputValueMode = 'derived') => {
            lastModeRef.current = mode;
            lastValueRef.current = next;
            if (mode === 'manual') {
                manualValueRef.current = next;
            } else {
                manualValueRef.current = undefined;
            }
            setHiddenInputValue(next);
        },
        [setHiddenInputValue]
    );

    return {
        hiddenInputRef,
        handleHiddenChange,
        setHiddenValue,
        lastModeRef,
        manualValueRef,
        lastValueRef,
    };
};

export type { HiddenInputControllerInternal };
export { useHiddenInputController };
